import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Search, Check, X, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientsPage = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    gst_number: "",
    status: "active"
  });

  const canVerify = user?.role === "admin" || user?.role === "super_admin";
  const canReject = user?.role === "admin" || user?.role === "super_admin";
  const canDelete = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClient) {
        await axios.put(`${API_URL}/clients/${selectedClient.id}`, formData);
        toast.success("Client updated successfully");
      } else {
        await axios.post(`${API_URL}/clients`, formData);
        toast.success("Client created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleVerify = async (clientId) => {
    try {
      await axios.post(`${API_URL}/clients/${clientId}/verify`);
      toast.success("Client verified successfully");
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API_URL}/clients/${selectedClient.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success("Client rejected");
      setRejectDialogOpen(false);
      setSelectedClient(null);
      setRejectReason("");
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rejection failed");
    }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      company_name: client.company_name,
      contact_person: client.contact_person,
      email: client.email,
      phone: client.phone,
      address: client.address,
      gst_number: client.gst_number || "",
      status: client.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/clients/${selectedClient.id}`);
      toast.success("Client deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const resetForm = () => {
    setSelectedClient(null);
    setFormData({
      company_name: "",
      contact_person: "",
      email: "",
      phone: "",
      address: "",
      gst_number: "",
      status: "active"
    });
  };

  const getVerificationBadge = (status) => {
    const styles = {
      pending: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
      verified: "border-green-500/50 text-green-400 bg-green-500/10",
      rejected: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.pending;
  };

  const filteredClients = clients.filter(client =>
    client.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Building2 className="w-8 h-8 text-accent" />
            Client Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage your business clients and customers</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Search */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search clients by company name, contact person, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="client-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Clients ({filteredClients.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Company Name</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Contact Person</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Email</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">GST Number</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Verification</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No clients found. Add your first client to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client) => (
                    <TableRow key={client.id} className="border-border table-row-hover" data-testid={`client-row-${client.id}`}>
                      <TableCell className="font-medium text-foreground">{client.company_name}</TableCell>
                      <TableCell className="text-muted-foreground">{client.contact_person}</TableCell>
                      <TableCell className="text-muted-foreground font-mono text-sm">{client.email}</TableCell>
                      <TableCell className="text-muted-foreground font-mono">{client.gst_number || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getVerificationBadge(client.verification_status || "pending")}>
                          {client.verification_status || "pending"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={client.status === "active" 
                            ? "border-green-500/50 text-green-400 bg-green-500/10" 
                            : "border-red-500/50 text-red-400 bg-red-500/10"
                          }
                        >
                          {client.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canVerify && (client.verification_status === "pending" || !client.verification_status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-400 hover:text-green-300"
                              onClick={() => handleVerify(client.id)}
                              data-testid={`verify-client-${client.id}`}
                              title="Verify"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {canReject && (client.verification_status === "pending" || !client.verification_status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => { setSelectedClient(client); setRejectDialogOpen(true); }}
                              data-testid={`reject-client-${client.id}`}
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(client)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => { setSelectedClient(client); setDeleteDialogOpen(true); }}
                              data-testid={`delete-client-${client.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedClient ? "Edit Client" : "Add New Client"}
            </DialogTitle>
            <DialogDescription>
              {selectedClient ? "Update client information" : "Enter the details of the new client"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="TechCorp Industries Pvt. Ltd."
                required
                data-testid="client-company-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Amit Sharma"
                required
                data-testid="client-contact-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="contact@techcorp.com"
                  required
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="+91 98765 43210"
                  required
                  data-testid="client-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="456 Business Park, Delhi"
                required
                data-testid="client-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gst_number">GST Number (Optional)</Label>
              <Input
                id="gst_number"
                value={formData.gst_number}
                onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="27AABCU9603R1ZM"
                data-testid="client-gst-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="client-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="client-submit-btn">
                {selectedClient ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Reject Client
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting client "{selectedClient?.company_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reject_reason">Rejection Reason</Label>
              <Input
                id="reject_reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="bg-secondary/50 border-border"
                placeholder="Enter reason for rejection..."
                data-testid="client-reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-client">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Client</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedClient?.company_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-client"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
